import { getFromLocalStorage } from '../utils.js';

const MIN_MOVEMENT_BEFORE_DRAG_START_PX = 1;
const TRANSITION_DURATION_SECONDS = 0.2;
const DROP_ANIMATION_DURATION = 500;

let currentPosition;
let dragStartPosition;
let draggedElement;
let shadowElements = [];
let draggedData;
let dragItemType;
let previousDroppableNode;
let previousIsAfter;
let isAfter;
let dropZoneItemType;
let dropZoneItemId;
let dropZoneRoleId;
let dropZoneSeqId;
let grabMouseUp;
let firstGrabElement;
let dropNode;

function getDragItemType(data) {
  let itemType = '';
  let item = null;

  if (Array.isArray(data)) {
    if (data.length) {
      //handle multiple dragged items
      item = data[0];
    }
  } else {
    item = data;
  }

  if (item) {
    if (item.hasOwnProperty('worksequences')) {
      itemType = 'role';
    } else if (item.hasOwnProperty('element_instances')) {
      itemType = 'sequence';
    } else if (item.hasOwnProperty('element_id')) {
      itemType = 'element';
    }
  }

  return itemType;
}

function postDropCleanUp() {
  dropZoneSeqId = undefined;
  dropZoneItemType = undefined;
  dropZoneRoleId = undefined;
  dropZoneItemId = undefined;
  shadowElements = [];
  draggedData = [];
  draggedElement = undefined;
  firstGrabElement = undefined;
}

function dragDropZone(node, params) {
  node.addEventListener('mousedown', handleMouseDown);

  const { draggableClass, droppableClass } = params ? params : {};

  function trs(property) {
    return `${property} ${TRANSITION_DURATION_SECONDS}s ease`;
  }

  function copyStylesFrom(fromElement, toElement) {
    const computedStyle = window.getComputedStyle(fromElement);
    Array.from(computedStyle)
      .filter(
        (s) =>
          s.startsWith('background') ||
          s.startsWith('padding') ||
          s.startsWith('font') ||
          s.startsWith('text') ||
          s.startsWith('align') ||
          s.startsWith('justify') ||
          s.startsWith('display') ||
          s.startsWith('flex') ||
          s.startsWith('border') ||
          s === 'opacity' ||
          s === 'color' ||
          s === 'list-style-type'
      )
      .forEach((s) =>
        toElement.style.setProperty(
          s,
          computedStyle.getPropertyValue(s),
          computedStyle.getPropertyPriority(s)
        )
      );
  }

  function createDraggableFrom(grabbed) {
    const elements = [];
    const dragContainer = document.createElement('div');
    let height = 0;

    if (draggedData && draggedData.length > 1) {
      for (const d of draggedData) {
        const e = document.getElementById(`${dragItemType}${d.id}`);
        if (e) {
          shadowElements.push(e);
          const dn = e.cloneNode(true);
          copyStylesFrom(e, dn);
          dragContainer.appendChild(dn);
        }
      }
    } else {
      if (grabbed) {
        const draggable = grabbed.cloneNode(true);
        copyStylesFrom(grabbed, draggable);
        dragContainer.appendChild(draggable);
        shadowElements.push(grabbed);
      }
    }

    if (shadowElements[0]) {
      const rect = shadowElements[0].getBoundingClientRect();
      const top = shadowElements.length === 1 ? rect.top : currentPosition.y;

      dragContainer.style.position = 'fixed';
      dragContainer.style.backgroundColor = 'white';
      dragContainer.style.margin = 0;
      dragContainer.style.boxSizing = 'border-box';
      dragContainer.style.top = `${top}px`;
      dragContainer.style.left = `${rect.left}px`;
      dragContainer.style.width = `${rect.width}px`;
      dragContainer.style.opacity = 0.8;
      dragContainer.style.transition = `${trs('width')}, ${trs(
        'height'
      )}, ${trs('background-color')}, ${trs('opacity')}, ${trs('color')} `;
      dragContainer.setAttribute('id', 'draggedElement');
    }

    return dragContainer;
  }

  function fireGrabEvent(event) {
    event.preventDefault();
    if (draggableClass && !grabMouseUp) {
      firstGrabElement = event.target.closest(`.${draggableClass}`);
      dragStartPosition = { x: event.clientX, y: event.clientY };
      currentPosition = { ...dragStartPosition };

      if (firstGrabElement) {
        event.stopPropagation();
        draggedData = JSON.parse(getFromLocalStorage('selectedListItems'));
        dragItemType = getDragItemType(draggedData);

        node.dispatchEvent(
          new CustomEvent('grabbed', {
            detail: {
              type: firstGrabElement.getAttribute('data-type'),
              id: parseInt(firstGrabElement.getAttribute('data-id')),
              nodes: shadowElements,
              draggedData,
              firstGrabElement,
            },
          })
        );
      }
    }
    window.addEventListener('mousemove', handlePreDragMouseMove);
  }

  function handleMouseDown(event) {
    grabMouseUp = false;

    window.addEventListener('mouseup', handlePreDragMouseUp);

    const draggable = !event.target.closest('.static');

    if (draggable) {
      fireGrabEvent(event);
    }
  }

  function ignoreDiscreteMovement(previousPosition, action) {
    if (previousPosition && currentPosition) {
      if (
        Math.abs(currentPosition.x - previousPosition.x) >=
          MIN_MOVEMENT_BEFORE_DRAG_START_PX ||
        Math.abs(currentPosition.y - previousPosition.y) >=
          MIN_MOVEMENT_BEFORE_DRAG_START_PX
      ) {
        window.removeEventListener('mousemove', handlePreDragMouseMove);
        window.removeEventListener('mouseup', handlePreDragMouseUp);

        action();
      }
    }
  }

  function handlePreDragMouseMove(event) {
    event.stopPropagation();
    event.preventDefault();
    currentPosition = { x: event.clientX, y: event.clientY };
    ignoreDiscreteMovement(dragStartPosition, handleDragStart);
  }

  function handlePreDragMouseUp(event) {
    event.stopPropagation();

    grabMouseUp = true;
    window.removeEventListener('mousemove', handlePreDragMouseMove);
    window.removeEventListener('mouseup', handlePreDragMouseUp);

    dragStartPosition = undefined;
    currentPosition = undefined;
  }

  function formatShadowElements(option) {
    shadowElements.forEach((e) => {
      e.style.opacity = option.opacity;
    });
  }

  function handleDragStart() {
    draggedElement = createDraggableFrom(firstGrabElement);

    node.dispatchEvent(
      new CustomEvent('dragStart', {
        detail: {
          dragItemType,
          draggedData,
        },
      })
    );

    function appendDraggedElementToBody() {
      if (!draggedElement.parentElement) {
        document.body.appendChild(draggedElement);
        draggedElement.focus();
        draggedElement.style.pointerEvents = 'none';
      }
    }

    window.requestAnimationFrame(appendDraggedElementToBody);

    window.addEventListener('mousemove', handleDragging);
    window.addEventListener('mouseup', handleDrop);
  }

  function handleDragging(event) {
    event.preventDefault();
    currentPosition = { x: event.clientX, y: event.clientY };
    draggedElement.style.transform = `translate3d(${
      currentPosition.x - dragStartPosition.x
    }px, ${currentPosition.y - dragStartPosition.y}px, 0)`;

    const elementAtPoint = document.elementFromPoint(
      event.clientX,
      event.clientY
    );

    if (elementAtPoint && !elementAtPoint.classList.contains('ignore')) {
      const { droppableZones } = params ? params : {};
      const sameTypeDropNode = elementAtPoint.closest(
        `.wrapper.${dragItemType}`
      );
      const parentDropNodeType =
        droppableZones[dragItemType] &&
        droppableZones[dragItemType].length === 2
          ? droppableZones[dragItemType][1]
          : null;
      const parentDropNode = elementAtPoint.closest(`.${parentDropNodeType}`);

      dropNode = sameTypeDropNode || parentDropNode;
      const dropNodeId = dropNode
        ? parseInt(dropNode.getAttribute('data-id'))
        : 0;

      if (
        dropNode &&
        dropNodeId &&
        !draggedData.find((d) => d.id === dropNodeId)
      ) {
        dropZoneItemType = dropNode.getAttribute('data-type');
        dropZoneItemId = parseInt(dropNode.getAttribute('data-id'));

        if (dropZoneItemType === 'sequence') {
          dropZoneRoleId = parseInt(dropNode.getAttribute('data-roleId'));
          dropZoneSeqId = dropZoneItemId;
        } else if (dropZoneItemType === 'element') {
          const sequenceNode = dropNode.closest('.sequence');
          if (sequenceNode) {
            dropZoneSeqId = parseInt(sequenceNode.getAttribute('data-id'));
            dropZoneRoleId = parseInt(sequenceNode.getAttribute('data-roleId'));
          }
        } else if (dropZoneItemType === 'role') {
          dropZoneRoleId = parseInt(dropNode.getAttribute('data-id'));
        }

        isAfter = checkIsAfter(dropNode);
        if (previousIsAfter != isAfter) {
          if (!draggedData.find((e) => e.id === dropZoneItemId)) {
            node.dispatchEvent(
              new CustomEvent('positionChange', {
                detail: {
                  dropZoneItemType,
                  dropZoneItemId,
                  dropZoneRoleId,
                  dropZoneSeqId,
                  dragItemType,
                  draggedData,
                  isAfter,
                  shadowElements,
                  dropRect: dropNode.getBoundingClientRect(),
                },
              })
            );
          }
        }
        previousIsAfter = isAfter;
      } else {
        // drag out
        previousIsAfter = undefined;
      }

      const container = node.closest('.body');

      if (container) {
        const draggedRect = draggedElement.getBoundingClientRect();

        if (
          currentPosition.y + 150 >
          document.body.getBoundingClientRect().bottom
        ) {
          container.scrollTop += 10;
        } else if (
          currentPosition.y - 50 <
          container.getBoundingClientRect().top
        ) {
          container.scrollTop -= 10;
        }
      }
    }
  }

  function checkIsAfter(dragOverNode) {
    const rect = dragOverNode.getBoundingClientRect();
    const center = rect.top + rect.height / 2;

    return currentPosition.y > center ? true : false;
  }

  function handleDrop(event) {
    window.removeEventListener('mousemove', handleDragging);
    window.removeEventListener('mouseup', handleDrop);
    event.stopPropagation();

    node.dispatchEvent(
      new CustomEvent('dropped', {
        detail: {
          dropZoneItemType,
          dropZoneItemId,
          dropZoneRoleId,
          dropZoneSeqId,
          dragItemType,
          draggedData,
          isAfter,
        },
      })
    );

    postDropCleanUp();
  }

  return {
    destroy() {
      node.removeEventListener('mousedowm', handleMouseDown);
    },
  };
}

export { dragDropZone };
